import type { ProfileData } from '@/types/profile';

/**
 * Vertical classification for sitemap and directory routing.
 *
 * These live in one place so `sitemap.ts` and the `/sitemaps/*.xml` segment
 * routes cannot drift apart. Drift is dangerous here: a vertical that is
 * present in one but not the other means its entity URLs are never submitted
 * to search engines, which is invisible until you diff the sitemap counts.
 *
 * Matching is deliberately broad. Profiles are created through many intake
 * forms across the platform's history, so the same vertical arrives with
 * different `profileType` wording. A missed match means a real business is
 * silently excluded from the index.
 */

const typeOf = (profile: ProfileData): string => String(profile.profileType || '').toLowerCase();

/** Stay / accommodation / hospitality. */
export const isStayProfile = (profile: ProfileData): boolean => {
  const type = typeOf(profile);
  return (
    type.includes('stay') ||
    type.includes('hospitality') ||
    type.includes('accommodation') ||
    type.includes('lodging') ||
    type.includes('guesthouse') ||
    type.includes('guest house') ||
    type.includes('hotel') ||
    type.includes('lodge') ||
    type.includes('backpacker') ||
    type.includes('farm stay') ||
    type.includes('self-catering') ||
    type.includes('self catering') ||
    Boolean(profile.stayLocationId) ||
    Boolean(profile.stayPropertyType)
  );
};

/** Restaurant / dining / cafe / food. */
export const isRestaurantProfile = (profile: ProfileData): boolean => {
  const type = typeOf(profile);
  return (
    type.includes('restaurant') ||
    type.includes('dining') ||
    type.includes('cafe') ||
    type.includes('coffee') ||
    type.includes('bakery') ||
    type.includes('catering') ||
    type.includes('eatery') ||
    type.includes('food') ||
    Boolean(profile.cuisines) ||
    Boolean(profile.restaurantCuisines)
  );
};

/** Fix-it / trades / contractor. */
export const isFixItProfile = (profile: ProfileData): boolean => {
  const type = typeOf(profile);
  return (
    type.includes('fix-it') ||
    type.includes('fixit') ||
    type.includes('fix it') ||
    type.includes('contractor') ||
    type.includes('trades') ||
    type.includes('plumb') ||
    type.includes('electric') ||
    type.includes('locksmith') ||
    type.includes('roofer') ||
    type.includes('solar') ||
    type.includes('handyman') ||
    type.includes('emergency')
  );
};

/** Retail / shopping / store. */
export const isRetailProfile = (profile: ProfileData): boolean => {
  const type = typeOf(profile);
  return type.includes('shopping') || type.includes('retail') || type.includes('store');
};

/** Job seeker / candidate. */
export const isJobSeekerProfile = (profile: ProfileData): boolean => {
  const type = typeOf(profile);
  return (
    type.includes('job seeker') ||
    type.includes('jobseeker') ||
    type.includes('candidate') ||
    Boolean(profile.jobTitle) ||
    Boolean(profile.professionalSummary)
  );
};

/** Domestic worker / helper. */
export const isDomesticWorkerProfile = (profile: ProfileData): boolean => {
  const type = typeOf(profile);
  return (
    type.includes('domestic') ||
    type.includes('helper') ||
    type.includes('nanny') ||
    type.includes('housekeeper') ||
    type.includes('cleaner') ||
    type.includes('gardener') ||
    type.includes('carer')
  );
};

/** Generic business owner, excluding the more specific verticals above. */
export const isGenericBusinessProfile = (profile: ProfileData): boolean => {
  const type = typeOf(profile);
  if (isStayProfile(profile) || isRestaurantProfile(profile) || isFixItProfile(profile) || isRetailProfile(profile)) {
    return false;
  }
  return type.includes('business') || type.includes('professional services') || type.includes('service provider');
};
